import { memo, useContext, useRef, useState } from 'react';
import { LottieContainer, AnimationContainer, RichText } from '@/sub-luca-plugin/compatible-components';
import { EveryoneIsAskItemIconSvg, ReplaceEveryoneIsAskSvg } from '@/sub-luca-plugin/compatible-components/svg-icons';
import { useDebounceFn } from '@/sub-luca-plugin/utils';
import { navigateExample, eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { IQuestionOfOcrSearch } from '..';
import { QaRecordsContext } from '../../helper';
import { IEveryoneIsAskItemInOcr } from '@/sub-luca-plugin/types';
import { query_everyone_is_asks } from '@/sub-luca-plugin/apis/qa';
import classNames from 'classnames';
import './index.scss';
import { IQaRecordItem } from '../..';

const QuestionOfOcrSearch = ({
    similarity,
    content,
    questionId,
    littleQuestionId,
    hideReplaceOcrResultBtn,
    recordId,
    everyAsks,
}: Omit<IQuestionOfOcrSearch, 'type' | 'role'>) => {
    const { getQaStoreByContext, toastInQa } = useContext(QaRecordsContext);
    const pageInfo = useRef({ pageNum: 1, pageSize: 3 });
    const noNextPage = useRef(everyAsks.length < 3);
    const [animation, setAnimation] = useState(false);
    const [everyoneIsAsks, setEveryoneIsAsks] = useState<IEveryoneIsAskItemInOcr[]>(everyAsks);

    const handleViewAnalysis = () => {
        // 跳转
        navigateExample.linkTo('problem_analysis', { questionId, littleQuestionId });
    };

    const handleViewDetail = (askId: number) => {
        // 跳转
        navigateExample.linkTo('gbot_detail', { id: askId });
    };

    const handleReplaceOcrResult = () => {
        if (!getQaStoreByContext()?.validateCanSend()) return;
        const { qaRecords, setQaRecords } = getQaStoreByContext()!;
        const index = qaRecords.findIndex((item: IQaRecordItem) => item.type === 'questionOfOcrSearch');
        setQaRecords((oldRecords: IQaRecordItem[]) => oldRecords.slice(0, index));
        eventBusExample.publish('DialogueArea', {
            type: 'replaceOcrResult',
            params: { recordType: 46, picAnswerNotQuestion: { recordId } },
        });
    };

    const { run: handleReplaceEveryoneIsAsk } = useDebounceFn(
        async () => {
            setAnimation(true);
            setTimeout(() => setAnimation(false), 300);
            if (noNextPage.current) {
                toastInQa?.('没有更多了');
                return;
            }
            pageInfo.current.pageNum++;
            const { result } = await query_everyone_is_asks({
                resourceType: 2,
                resourceId: littleQuestionId || questionId,
                ...pageInfo.current,
            });
            const { list = [] } = result || {};
            noNextPage.current = list.length < 3;
            if (list.length > 0) {
                setEveryoneIsAsks(list);
            } else {
                toastInQa?.('没有更多了');
            }
        },
        { wait: 500 }
    );

    return (
        <div className="respondent-container">
            <AnimationContainer
                className="question-of-ocr-search"
                type="enter_from_the_left"
                canExcuteAimation={getQaStoreByContext()?.qaConfig.canExcuteAimation}
            >
                <div className="question-of-ocr-search__title">
                    <LottieContainer
                        className="question-of-ocr-search__title-icon"
                        type="starsTwinkle"
                        excuteType="autoPlayByInit"
                        width={22}
                        delay={1000}
                    />
                    <span>已为你找到这道题目</span>
                </div>
                <div className="question-of-ocr-search__relative-container">
                    {!hideReplaceOcrResultBtn && (
                        <div className="question-of-ocr-search__replace-ocr-result" onClick={handleReplaceOcrResult} />
                    )}
                    <div className="question-of-ocr-search__container">
                        <div className="question-of-ocr-search__similarity">相似度 {similarity}%</div>
                        <RichText className="question-of-ocr-search__content" content={content} />
                    </div>
                    <div className="question-of-ocr-search__view-analysis" onClick={handleViewAnalysis}>
                        查看答案&解析
                    </div>
                </div>
                {everyoneIsAsks.length > 0 && (
                    <div className="ocr-everyone-is-ask">
                        <div className="ocr-everyone-is-ask__header">
                            <span>大家都在问</span>
                            <div className="ocr-everyone-is-ask__replace" onClick={handleReplaceEveryoneIsAsk}>
                                <ReplaceEveryoneIsAskSvg
                                    className={classNames('ocr-everyone-is-ask__icon', animation && 'ocr-everyone-is-ask__icon--animation')}
                                />
                                <span>换一换</span>
                            </div>
                        </div>
                        {everyoneIsAsks.map(item => (
                            <div key={item.askId} className="ocr-everyone-is-ask__item" onClick={() => handleViewDetail(item.askId)}>
                                <EveryoneIsAskItemIconSvg className="ocr-everyone-is-ask__item-icon" />
                                <span className="ocr-everyone-is-ask__content">{item.content}</span>
                            </div>
                        ))}
                    </div>
                )}
            </AnimationContainer>
        </div>
    );
};

export default memo(QuestionOfOcrSearch);
